import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell, Sector } from 'recharts';
import { useTheme } from '../ui/theme-provider';

// Define some colors for the charts
const COLORS_LIGHT = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#a4de6c', '#d0ed57'];
const COLORS_DARK = ['#a3a0f0', '#92dabc', '#ffd678', '#ff8f30', '#b4e87c', '#e0f767'];

const ExpenseChart = ({ data, chartType, theme }) => {
  // Log received props immediately
  console.log(`ExpenseChart received props - chartType: ${chartType}, theme: ${theme}, data:`, data);

  // Check data structure right at the beginning (similar to the error line 28)
  if (!data || !Array.isArray(data) || data.length === 0 || !data.every(item => typeof item === 'object' && item !== null && 'label' in item && 'value' in item)) {
    // Log the exact data that failed the check
    console.error("ExpenseChart: Invalid or empty data structure received:", data); 
    // Render nothing or a placeholder if data is invalid
    return <div className="flex items-center justify-center h-full text-gray-500">Invalid chart data</div>; 
  }

  const isDarkMode = theme === 'dark';
  const COLORS = isDarkMode ? COLORS_DARK : COLORS_LIGHT;
  const tickColor = isDarkMode ? '#a3a0f0' : '#666'; // Example theme-based color

  const formatCurrencyTick = (value) => `£${value.toLocaleString()}`;

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Only show label if percent is significant
    if (percent < 0.05) return null; 

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="10px">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      {chartType === 'bar' ? (
        <BarChart data={data} margin={{ top: 5, right: 10, left: 20, bottom: 5 }}>
          <XAxis 
            dataKey="label" 
            fontSize="10px" 
            tick={{ fill: tickColor }} 
            interval={0} // Show all labels initially, adjust if too crowded
            angle={-30} // Angle labels slightly if they overlap
            textAnchor="end" // Anchor angled labels correctly
            height={50} // Increase height to accommodate angled labels
          />
          <YAxis 
            fontSize="10px" 
            tick={{ fill: tickColor }} 
            tickFormatter={formatCurrencyTick}
          />
          <Tooltip 
            cursor={{ fill: isDarkMode ? 'rgba(163, 160, 240, 0.1)' : 'rgba(136, 132, 216, 0.1)' }}
            contentStyle={{ 
              backgroundColor: isDarkMode ? '#2A2A3E' : '#ffffff', 
              borderColor: isDarkMode ? '#a3a0f0' : '#cccccc',
              fontSize: '12px'
            }}
            formatter={(value) => [`£${value.toFixed(2)}`, 'Amount']}
          />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          <Bar dataKey="value" name="Total Expenses" fill={isDarkMode ? '#a3a0f0' : '#8884d8'} radius={[4, 4, 0, 0]} />
        </BarChart>
      ) : (
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            // label={renderCustomizedLabel} // Optional: Add labels inside slices
            outerRadius="80%"
            fill="#8884d8"
            dataKey="value"
            nameKey="label"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip 
             contentStyle={{ 
              backgroundColor: isDarkMode ? '#2A2A3E' : '#ffffff', 
              borderColor: isDarkMode ? '#a3a0f0' : '#cccccc',
              fontSize: '12px'
            }}
            formatter={(value, name) => [`£${value.toFixed(2)}`, name]}
          />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
        </PieChart>
      )}
    </ResponsiveContainer>
  );
};

export default ExpenseChart;
